/**
 * Validarea formularului de înregistrare, separat de ecran.
 *
 * The rules are the server's, restated here so the form can refuse before a
 * round trip: minimum age 2, a child needs an email OR a username, a minor
 * needs their guardian's details and consent, passwords at least 6 characters.
 * Keeping them in a pure module is what makes them testable — the previous
 * registration form had its own age list that stopped at six while the server
 * accepted three, and nothing on screen explained why.
 *
 * Usernames: lowercase letters, digits, dot and underscore, 3-30 characters.
 * Matches `isValidUsername` in the backend's registrationRules.
 */

import { MIN_ATHLETE_AGE, MAX_CHILD_AGE } from "./athleteAge";

/**
 * The oldest somebody registering THEMSELVES can be.
 *
 * Mirrors MAX_ATHLETE_AGE in the backend's registrationRules, which is 100 —
 * not the 18 in `athleteAge.ts`, which bounds the age PICKERS a parent or a
 * club uses for a child. Conflating the two caps an adult who plays in a
 * senior team at eighteen and refuses the account of anybody older, which is
 * the same class of bug as the picker that used to start at six: the form, not
 * the server, becomes the real limit.
 */
export const MAX_SELF_AGE = 100;

export const MIN_PASSWORD = 6;
export const ADULT_AGE = 18;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_RE = /^[a-z0-9_.]{3,30}$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(String(value || "").trim());
}

export function normalizeUsername(value: string): string {
  return String(value || "").trim().toLowerCase();
}

export function isValidUsername(value: string): boolean {
  return USERNAME_RE.test(normalizeUsername(value));
}

export type ParentForm = {
  parent_name: string;
  parent_email: string;
  parent_phone: string;
  password: string;
  child_first_name: string;
  child_age: string;
  child_email: string;
  child_username: string;
  child_password: string;
  terms: boolean;
};

export type AthleteForm = {
  first_name: string;
  age: string;
  email: string;
  username: string;
  password: string;
  guardian_name: string;
  guardian_email: string;
  guardian_phone: string;
  guardian_consent: boolean;
  terms: boolean;
};

/** First problem found, in the order somebody fills the form in. Null = valid. */
export function validateParentForm(f: ParentForm): string | null {
  if (!f.parent_name.trim()) return "Scrie numele tău.";
  if (!isValidEmail(f.parent_email)) return "Adresa ta de email nu pare validă.";
  if (f.password.length < MIN_PASSWORD) return `Parola ta trebuie să aibă minim ${MIN_PASSWORD} caractere.`;
  if (!f.child_first_name.trim()) return "Scrie numele copilului.";

  const age = Number(f.child_age);
  if (!Number.isFinite(age) || age < MIN_ATHLETE_AGE || age > MAX_CHILD_AGE) {
    return `Vârsta copilului trebuie să fie între ${MIN_ATHLETE_AGE} și ${MAX_CHILD_AGE} ani. La 18 ani sportivul își face singur cont.`;
  }

  const hasEmail = !!f.child_email.trim();
  const hasUsername = !!f.child_username.trim();
  if (!hasEmail && !hasUsername) {
    return "Copilul are nevoie de un email sau de un username cu care să intre în aplicație.";
  }
  if (hasEmail && !isValidEmail(f.child_email)) return "Adresa de email a copilului nu pare validă.";
  if (hasEmail && f.child_email.trim().toLowerCase() === f.parent_email.trim().toLowerCase()) {
    // Two accounts cannot share an address, and this is a very easy mistake to
    // make: the parent types their own email twice without thinking.
    return "Copilul nu poate avea aceeași adresă de email ca tine. Lasă câmpul gol și alege-i un username.";
  }
  if (hasUsername && !isValidUsername(f.child_username)) {
    return "Username-ul poate avea 3-30 de caractere: litere mici, cifre, punct sau underscore.";
  }
  if (f.child_password.length < MIN_PASSWORD) {
    return `Parola copilului trebuie să aibă minim ${MIN_PASSWORD} caractere.`;
  }
  if (!f.terms) return "Trebuie să accepți Termenii și Politica de confidențialitate.";
  return null;
}

export function validateAthleteForm(f: AthleteForm): string | null {
  if (!f.first_name.trim()) return "Scrie-ți numele.";

  const age = Number(f.age);
  if (!Number.isInteger(age) || age < MIN_ATHLETE_AGE || age > MAX_SELF_AGE) {
    return `Vârsta trebuie să fie între ${MIN_ATHLETE_AGE} și ${MAX_SELF_AGE} ani.`;
  }

  const hasEmail = !!f.email.trim();
  const hasUsername = !!f.username.trim();
  if (!hasEmail && !hasUsername) return "Ai nevoie de un email sau de un username.";
  if (hasEmail && !isValidEmail(f.email)) return "Adresa de email nu pare validă.";
  if (hasUsername && !isValidUsername(f.username)) {
    return "Username-ul poate avea 3-30 de caractere: litere mici, cifre, punct sau underscore.";
  }
  if (f.password.length < MIN_PASSWORD) return `Parola trebuie să aibă minim ${MIN_PASSWORD} caractere.`;

  // A minor cannot consent for themselves. The server answers 422 without
  // these, so the form must ask for them rather than let the person fail.
  if (age < ADULT_AGE) {
    if (!f.guardian_name.trim()) return "Pentru un sportiv minor e obligatoriu numele părintelui sau tutorelui.";
    if (!isValidEmail(f.guardian_email)) return "Emailul părintelui sau tutorelui nu pare valid.";
    if (hasEmail && f.guardian_email.trim().toLowerCase() === f.email.trim().toLowerCase()) {
      return "Emailul tutorelui trebuie să fie diferit de al tău.";
    }
    if (!f.guardian_consent) return "Părintele sau tutorele trebuie să își dea acordul.";
  }
  if (!f.terms) return "Trebuie să accepți Termenii și Politica de confidențialitate.";
  return null;
}

/** The body `/auth/register-parent` expects. Free plan only from here. */
export function parentPayload(f: ParentForm) {
  return {
    parent: {
      name: f.parent_name.trim(),
      email: f.parent_email.trim().toLowerCase(),
      password: f.password,
      ...(f.parent_phone.trim() ? { phone: f.parent_phone.trim() } : {}),
    },
    child: {
      first_name: f.child_first_name.trim(),
      age: Number(f.child_age),
      ...(f.child_email.trim() ? { email: f.child_email.trim().toLowerCase() } : {}),
      ...(f.child_username.trim() ? { username: normalizeUsername(f.child_username) } : {}),
      password: f.child_password,
    },
    plan: "free",
    billing_period: "month",
    terms_accepted: true,
    privacy_accepted: true,
    source: "app:parinti",
  };
}

/** The body `/auth/register-athlete` expects. */
export function athletePayload(f: AthleteForm) {
  const age = Number(f.age);
  const minor = age < ADULT_AGE;
  return {
    first_name: f.first_name.trim(),
    age,
    ...(f.email.trim() ? { email: f.email.trim().toLowerCase() } : {}),
    ...(f.username.trim() ? { username: normalizeUsername(f.username) } : {}),
    password: f.password,
    ...(minor
      ? {
          guardian_name: f.guardian_name.trim(),
          guardian_email: f.guardian_email.trim().toLowerCase(),
          ...(f.guardian_phone.trim() ? { guardian_phone: f.guardian_phone.trim() } : {}),
          guardian_consent: true,
        }
      : {}),
    plan: "free",
    billing_period: "month",
    terms_accepted: true,
    privacy_accepted: true,
    source: "app:sportivi",
  };
}
